/* eslint-disable react/react-in-jsx-scope */
import { FormControlLabel, Grid, createStyles, makeStyles } from "@material-ui/core";
import Checkbox, { CheckboxProps } from "@material-ui/core/Checkbox";
import clsx from "clsx";
import useText from "constants/text";
import { IProductPick, InationalTransportPoint, ItemAtPoint, isSameItem } from "contexts/RequestQuoteContext";
import { Product } from "generated/graphql";
import React, { useCallback, useMemo, useState } from "react";
import Table from "components/TableCatalog/Table";
import { Column } from "components/Table/Table";
import { COLORS } from "constants/colors";
import { CardProduct, ContainerProduct, ProductListHeader } from "./CatalogStyles";
import CatalogCounter from "../../Catalog/CatalogCounter";
import ItemCatalog from "./ItemCatalog";

export const useStyles = makeStyles(() =>
  createStyles({
    root: {
      "& .MuiTypography-body1": {
        display: "flex",
      },
    },
    image: { height: "30px", width: "30px", borderRadius: 5 },
    text: { color: "#757a93", fontWeight: "bold", fontSize: "13px", textAlign: "left" },
    description: { color: "#757a93", fontWeight: 500, fontSize: "12px", display: "flex" },
    icon: {
      borderRadius: 3,
      width: 16,
      height: 16,
      boxShadow: "inset 0 0 0 1px rgba(16,22,26,.2), inset 0 -1px 0 rgba(16,22,26,.1)",
      backgroundColor: "#f5f8fa",
      backgroundImage: "linear-gradient(180deg,hsla(0,0%,100%,.8),hsla(0,0%,100%,0))",
      "$root.Mui-focusVisible &": {
        outline: "2px auto rgba(19,124,189,.6)",
        outlineOffset: 2,
      },
      "input:hover ~ &": {
        backgroundColor: "#ebf1f5",
      },
      "input:disabled ~ &": {
        boxShadow: "none",
        background: "rgba(206,217,224,.5)",
      },
    },
    checkedIcon: {
      backgroundColor: "#399ebf",
      backgroundImage: "linear-gradient(180deg,hsla(0,0%,100%,.1),hsla(0,0%,100%,0))",
      "&:before": {
        display: "block",
        width: 16,
        height: 16,
        backgroundImage:
          "url(\"data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3E%3Cpath" +
          " fill-rule='evenodd' clip-rule='evenodd' d='M12 5c-.28 0-.53.11-.71.29L7 9.59l-2.29-2.3a1.003 " +
          "1.003 0 00-1.42 1.42l3 3c.18.18.43.29.71.29s.53-.11.71-.29l5-5A1.003 1.003 0 0012 5z' fill='%23fff'/%3E%3C/svg%3E\")",
        content: '""',
      },
      "input:hover ~ &": {
        backgroundColor: "#106ba3",
      },
    },
    productListHeader: {
      color: `${COLORS.primary}`,
      fontSize: "17px",
      paddingLeft: "3%",
      width: "100%",
    },
    productItem: {
      width: "100%",
      position: "relative",
      padding: 0,
      margin: 0,
      display: "flex",
      flexDirection: "row",
    },
    productWrapper: {
      width: "98%",
      position: "absolute",
      height: "100%",
      bottom: 0,
      paddingRight: 9,
      fontSize: 12,
      fontWeight: "bold",
    },
    name: {
      paddingLeft: "5%",
    },
  })
);

const useCatalogItemsColumns = (
  text: any,
  selectedProducts: any[],
  newProductsChecked: ItemAtPoint[],
  productsToPick: ItemAtPoint[],
  point: InationalTransportPoint,
  isForDelivery: boolean,
  classes: any,
  onChangeCountValue: (item: Product, counter: number) => void
): Column<Partial<IProductPick>>[] => [
  {
    field: "product",
    title: text.all.article_name,
    render: ({ name, description, photoUrl }) => (
      <Grid container direction="row">
        <div>
          <img src={photoUrl} alt="product" className={classes.image} />
        </div>
        <div className={classes.name}>
          <span className={classes.text}>{name}</span>
          <span className={classes.description}>{description}</span>
        </div>
      </Grid>
    ),
  },
  {
    field: "reference",
    title: text.all.reference,
    render: ({ reference }) => <span className={classes.text}>{reference}</span>,
  },
  {
    field: "size",
    title: text.all.size,
    render: ({ weight }) => <span className={classes.text}>{weight}</span>,
  },
  {
    field: "price",
    title: text.all.price,
    render: ({ value }) => <span className={classes.text}>{value}</span>,
  },
  {
    field: "quantity",
    title: text.all.quantity,
    render: (product) =>
      selectedProducts.includes(product._id) && (
        <CatalogCounter
          product={product}
          isForDelivery={isForDelivery}
          point={point}
          productsChecked={newProductsChecked}
          productsToPick={productsToPick}
          onCounterChanged={false}
          onChangeCountValue={onChangeCountValue}
        />
      ),
  },
];

function StyledCheckbox(props: CheckboxProps) {
  const classes = useStyles();

  return (
    <Checkbox
      className={classes.root}
      disableRipple
      color="default"
      checkedIcon={<span className={clsx(classes.icon, classes.checkedIcon)} />}
      icon={<span className={classes.icon} />}
      inputProps={{ "aria-label": "decorative checkbox" }}
      {...props}
    />
  );
}

interface IListCatalogue {
  allProducts: IProductPick[];
  productsChecked?: ItemAtPoint[];
  onChange: (item: IProductPick) => void;
  onProductCounterChanged: (item: Product, counter: number) => void;
  isForDelivery?: boolean;
  productsToPick?: ItemAtPoint[];
  point?: InationalTransportPoint;
  setNewProductsChecked?: React.Dispatch<React.SetStateAction<ItemAtPoint[]>>;
  newProductsChecked?: ItemAtPoint[];
  productsDelivered?: ItemAtPoint[];
}

export default function ListCatalog({
  allProducts = [],
  productsChecked,
  onChange,
  onProductCounterChanged,
  isForDelivery = false,
  productsToPick,
  point,
  setNewProductsChecked,
  newProductsChecked,
  productsDelivered,
}: IListCatalogue) {
  const styles = useStyles();
  const { text } = useText();

  const handleProductChecked = (product: IProductPick) => {
    const prod = JSON.parse(JSON.stringify(product));
    prod.refPickupDelivery = product._id;
    onChange(prod);
  };

  const handleProductCounter = (product: IProductPick, counter: number) => {
    onProductCounterChanged(product, counter);
  };
  const onChangeCountValue = (product: IProductPick, counter: number) => {
    onProductCounterChanged(product, counter);
  };
  const compareById = (item: ItemAtPoint, product: IProductPick) => item?.product?._id === product?._id;
  const compareByIdAndPickup = (item: ItemAtPoint, product: IProductPick) =>
    item?.product?._id === product?._id && product?.pickup === item?.product?.pickup;

  const isChecked = useCallback(
    (product: IProductPick) =>
      isForDelivery
        ? productsChecked?.findIndex((p) => compareByIdAndPickup(p, product)) !== -1
        : productsChecked?.findIndex((p) => compareById(p, product)) !== -1,
    [productsChecked]
  );

  const getAlreadyDeliveredCount = (product: IProductPick) =>
    point?.itemsToDelivered?.find((i) => compareByIdAndPickup(i, product))?.counter || 0;

  const getProductCounterMax = useCallback(
    (product: any) =>
      isForDelivery
        ? getAlreadyDeliveredCount(product) +
          productsToPick?.find((p) => isSameItem(p?.product, product) && product?.pickup === p?.product?.pickup)
            ?.remainder!
        : // eslint-disable-next-line eqeqeq
          productsToPick?.find((p) => p?.product?._id == product?._id)?.counter,
    [productsToPick]
  );

  const hasAvailableArticleToDelivery = (product: IProductPick) =>
    isForDelivery ? getProductCounterMax(product) > 0 : true;
  const getProductCounter = useCallback(
    (product: IProductPick) => {
      if (!isChecked(product)) {
        return 0;
      }
      return isForDelivery
        ? productsChecked?.find((p) => compareByIdAndPickup(p, product))?.counter
        : productsChecked?.find((p) => compareById(p, product))?.counter;
    },
    [productsChecked]
  );

  const getCheckedIds = useMemo(() => {
    if (productsChecked?.length > 0 && allProducts?.length > 0) {
      const allIds = productsChecked?.map((prod) => prod?.product?._id);
      const catalogIds = allProducts?.map((prod) => prod._id);
      return allIds.filter((id) => catalogIds.includes(id));
    }
  }, [productsChecked, allProducts]);
  const getNonSavedCheckedIds = useMemo(() => {
    if (productsToPick?.length > 0 && allProducts?.length > 0) {
      const allIds = productsToPick?.map((prod) => prod?.product?._id);
      const catalogIds = allProducts?.map((prod) => prod._id);
      return allIds.filter((id) => catalogIds.includes(id));
    }
  }, [productsToPick, allProducts]);
  const [selectedProducts, setSelectedProducts] = useState(getCheckedIds || getNonSavedCheckedIds);
  const renderProducts = useCallback(
    () =>
      allProducts?.map((product: IProductPick) =>
        product && hasAvailableArticleToDelivery(product) ? (
          <CardProduct key={product?._id}>
            <FormControlLabel
              control={
                <StyledCheckbox
                  checked={isChecked(product)}
                  onChange={() => handleProductChecked(product)}
                  name={product?.name}
                />
              }
              className={styles.productItem}
              label={
                <ItemCatalog
                  product={product}
                  counter={getProductCounter(product)}
                  isChecked={isChecked(product)}
                  onChangeCountValue={handleProductCounter}
                  isForDelivery={isForDelivery}
                  maxCountValue={getProductCounterMax(product)}
                />
              }
            />
          </CardProduct>
        ) : null
      ),
    [productsChecked]
  );

  const columns = useCatalogItemsColumns(
    text,
    selectedProducts,
    newProductsChecked,
    productsToPick,
    point,
    isForDelivery,
    styles,
    onChangeCountValue
  );
  return (
    <ContainerProduct wrap="wrap" className={styles.root}>
      <ProductListHeader>
        <Grid className={styles.productListHeader}>
          <Grid container xs={12}>
            <Grid item xs={5}>
              {text.all.article_name}
            </Grid>
            <Grid item xs={2}>
              {text.all.reference}
            </Grid>
            <Grid item xs={1}>
              {text.all.size}
            </Grid>
            <Grid item xs={2}>
              {text.all.price}
            </Grid>
            <Grid item xs={2}>
              {text.all.quantity}
            </Grid>
          </Grid>
        </Grid>
      </ProductListHeader>
      {renderProducts()}
    </ContainerProduct>
  );
}
